import * as Yup from "yup";
import { RequirementDefinitionTemplateForm } from "../../types";
import { FormPartialAcceptWingspanMasterPolicy } from "../FormPartialAcceptWingspanMasterPolicy";
import { WarningWingspanIsNotAgent } from "../WarningWingspanIsNotAgent";
import { IAcceptedInsuranceCoverage } from "../../../../../../services/api/insurance/monitorSettings/types";
import {
  FormPartialWorkInjury,
  WORKER_OCCUPATIONAL_ACCIDENT_AD_AND_D_MINIMUM_COMMON,
  WORKER_OCCUPATIONAL_ACCIDENT_MINIMUM_COMMON
} from "../FormPartialWorkInjury";
import { CreateRequirementDefinitionInsuranceCoveragePartialForm } from "../../../../modules/InsuranceCoverage/createRequirementDefinition/types";
import {
  WorkInjuryAcceptedCoverageType,
  WorkInjuryEmployerLiabilityBundle
} from "@wingspanhq/benefits/dist/src/lib/interfaces/insuranceRequirement";
import { selectorWorkersCompLimitMapper } from "../../../../selectors/selectorWorkersCompLimitMapper";

type WorkInjuryValues = {
  acceptWingspanMasterPolicy: boolean;
  coverageType: WorkInjuryAcceptedCoverageType;
  workersCompLimitMinimum?: WorkInjuryEmployerLiabilityBundle;
  occAccidentMedicalMinimum?: string;
  occAccidentDeathMinimum?: string;
};

/*
https://github.com/wingspanHQ/benefits/blob/staging/src/lib/interfaces/insuranceRequirement.ts
 */

export const WORK_INJURY_FORM: RequirementDefinitionTemplateForm<
  WorkInjuryValues,
  CreateRequirementDefinitionInsuranceCoveragePartialForm
> = {
  getDefaultValues: template => ({
    name: template.name,
    acceptWingspanMasterPolicy: true,
    coverageType: WorkInjuryAcceptedCoverageType.OccupationalAccident,
    workersCompLimitMinimum:
      WorkInjuryEmployerLiabilityBundle.OneHundredFiveHundredOneHundred,
    occAccidentMedicalMinimum: `${WORKER_OCCUPATIONAL_ACCIDENT_MINIMUM_COMMON}`,
    occAccidentDeathMinimum: `${WORKER_OCCUPATIONAL_ACCIDENT_AD_AND_D_MINIMUM_COMMON}`
  }),
  mapFormData(values) {
    const coverages: IAcceptedInsuranceCoverage[] = [];

    const {
      acceptWingspanMasterPolicy,
      coverageType,
      workersCompLimitMinimum,
      occAccidentMedicalMinimum,
      occAccidentDeathMinimum,
      ...other
    } = values;

    if (
      coverageType === WorkInjuryAcceptedCoverageType.WorkersCompensation ||
      coverageType === WorkInjuryAcceptedCoverageType.Either
    ) {
      coverages.push({
        policyType: "WorkersCompensation",
        appliedProvisions: [],
        limits: selectorWorkersCompLimitMapper(workersCompLimitMinimum),
        acceptedSourceTypes: ["InsuranceImport"]
      });
    }

    if (
      coverageType === WorkInjuryAcceptedCoverageType.OccupationalAccident ||
      coverageType === WorkInjuryAcceptedCoverageType.Either
    ) {
      coverages.push({
        policyType: "OccupationalAccident",
        appliedProvisions: [],
        limits: [
          {
            limitType: "MedicalExpensePerPerson",
            amount: Number(occAccidentMedicalMinimum),
            currency: "USD"
          },
          {
            limitType: "PerAccident",
            amount: Number(occAccidentDeathMinimum),
            currency: "USD"
          }
        ],
        acceptedSourceTypes: ["InsuranceImport"]
      });
    }

    if (acceptWingspanMasterPolicy) {
      // only occupational accident master policy is supported by wingspan for now
      const occupationalAccident = coverages.find(
        c => c.policyType === "OccupationalAccident"
      );
      if (occupationalAccident) {
        // add plan enrollment to the already existing occupational accident coverage
        occupationalAccident.acceptedSourceTypes?.push("PlanEnrollment");
      } else {
        // add a coverage which accepts only plan enrollment for occupational accident
        coverages.push({
          policyType: "OccupationalAccident",
          appliedProvisions: [],
          limits: [],
          acceptedSourceTypes: ["PlanEnrollment"]
        });
      }
    }

    return {
      ...other,
      coverages
    };
  },
  partialForm: (
    <>
      <WarningWingspanIsNotAgent />
      <FormPartialAcceptWingspanMasterPolicy switchLabel="Accept Wingspan's OAI Master Policy" />
      <FormPartialWorkInjury />
    </>
  ),
  validationSchema: Yup.object().shape({
    acceptWingspanMasterPolicy: Yup.boolean().required("Required"),
    coverageType: Yup.string()
      .oneOf(Object.values(WorkInjuryAcceptedCoverageType))
      .required("Required") as Yup.AnySchema,
    workersCompLimitMinimum: Yup.string().when("coverageType", {
      is: (val: WorkInjuryAcceptedCoverageType) =>
        val === WorkInjuryAcceptedCoverageType.WorkersCompensation ||
        val === WorkInjuryAcceptedCoverageType.Either,
      then: Yup.string()
        .oneOf(Object.values(WorkInjuryEmployerLiabilityBundle))
        .required("Required"),
      otherwise: Yup.string()
    }) as Yup.AnySchema,
    occAccidentDeathMinimum: Yup.string().when("coverageType", {
      is: (val: WorkInjuryAcceptedCoverageType) =>
        val === WorkInjuryAcceptedCoverageType.OccupationalAccident ||
        val === WorkInjuryAcceptedCoverageType.Either,
      then: Yup.string().required("Required"),
      otherwise: Yup.string()
    }) as Yup.AnySchema,
    occAccidentMedicalMinimum: Yup.string().when("coverageType", {
      is: (val: string) =>
        val === WorkInjuryAcceptedCoverageType.OccupationalAccident ||
        val === WorkInjuryAcceptedCoverageType.Either,
      then: Yup.string().required("Required"),
      otherwise: Yup.string()
    })
  })
};
